import React, { useState, FormEvent } from 'react';
import { Typography, Button, styled, lighten } from '@mui/material';
import LoginForm from './LoginForm';
import { Link, Navigate } from 'react-router-dom';
import history from 'services/history';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from 'hooks/auth/AuthElement';
import Loading from 'components/loading/Loading';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
}));

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: 30,
  flexDirection: 'column',
  backgroundColor: lighten(theme.palette.primary.main, 0.9),
  borderRadius: 16,
  border: 'none',
  minWidth: 350,
  margin: 20,
  position: 'relative',
  boxShadow: theme.shadows[5],
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 300,
    padding: 30,
  },
}));

const Header = styled('div')({
  padding: '0 0 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  '& h5': {
    marginTop: 20,
  },
});

const Actions = styled('div')({
  display: 'flex',
  padding: '50px 0',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  '& > .forgot': {
    marginTop: 30,
  },
});

const Logo = styled('img')({
  width: 120,
  borderRadius: '50%',
});

const LogoWrapper = styled('div')({
  width: 120,
  height: 120,
  marginTop: -80,
});

export type LoginValidation = {
  username?: string;
  password?: string;
};

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<LoginValidation>({});
  const auth = useAuth();

  function handleSubmit() {
    setLoading(true);

    auth
      .login(username, password)
      .then(() => {
        history.push('/');
      })
      .catch(err => {
        setLoading(false);

        console.error(err);

        if (err.response && err.response.status === 401) {
          toast.error(err.response.error);
          setValidation({
            username: 'Nome de usuário está correto?',
            password: 'Senha está correta?',
          });
          return;
        }

        if (err.response) {
          toast.error('Não foi possível fazer login');
          console.error(err.response);
          return;
        }

        toast.error('Não foi possível conectar ao servidor');
      });
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    setValidation({});

    const schema = yup.object().shape({
      password: yup.string().required('Informe uma senha'),
      username: yup.string().required('Informe o nome de usuário'),
    });

    schema
      .validate({ username, password })
      .then(() => {
        handleSubmit();
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  return (
    <>
      {auth.checkAuth() ? (
        <Navigate to="/" />
      ) : (
        <Container>
          <Content>
            {loading && <Loading />}
            <Header>
              <LogoWrapper>
                <a href={process.env.PUBLIC_URL}>
                  <Logo src="/universo-plastica.PNG" alt="Universo Plástica Logo" />
                </a>
              </LogoWrapper>
              <Typography align="center" variant="h5">
                Login
              </Typography>
            </Header>
            <form onSubmit={handleValidation}>
              <LoginForm
                username={username}
                password={password}
                setPassword={setPassword}
                setUsername={setUsername}
                validation={validation}
              />
            </form>
            <Actions>
              <Button
                onClick={() => handleValidation(undefined)}
                color="primary"
                variant="contained"
                disableElevation
                fullWidth
                disabled={loading}
              >
                Entrar
              </Button>
              <div className="forgot">
                <Link to="/forgot">Esqueci minha senha</Link>
              </div>
            </Actions>
          </Content>
        </Container>
      )}
    </>
  );
};

export default Login;
